import * as React from 'react';
import { Checkbox } from './Checkbox';
import type { CheckboxSize } from './Checkbox';
import { useGroupState, GroupFieldset } from '../shared';
import type { GroupOption } from '../shared';

/**
 * Option type for CheckboxGroup
 */
export type CheckboxGroupOption = GroupOption;

/**
 * CheckboxGroup component props
 */
export interface CheckboxGroupProps {
  /**
   * Label for the checkbox group
   */
  label?: string;
  /**
   * Array of options to display
   */
  options: CheckboxGroupOption[];
  /**
   * Currently selected values (controlled mode)
   */
  value?: string[];
  /**
   * Default selected values (uncontrolled mode)
   */
  defaultValue?: string[];
  /**
   * Callback when selection changes
   */
  onChange?: (values: string[]) => void;
  /**
   * Layout orientation of the checkboxes
   * @default 'vertical'
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Size variant of the checkboxes
   * @default 'md'
   */
  size?: CheckboxSize;
  /**
   * Whether the group is in an error state
   */
  error?: boolean;
  /**
   * Error message to display
   */
  errorMessage?: string;
  /**
   * Whether the group is required
   */
  required?: boolean;
  /**
   * Whether all checkboxes in the group are disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Helper text displayed below the group
   */
  helperText?: string;
  /**
   * ID for the group (used for accessibility)
   */
  id?: string;
  /**
   * Name attribute for the checkbox inputs
   */
  name?: string;
}

/**
 * CheckboxGroup component for selecting multiple options.
 *
 * @example
 * // Basic vertical group
 * <CheckboxGroup
 *   label="Select options"
 *   options={[
 *     { value: 'a', label: 'Option A' },
 *     { value: 'b', label: 'Option B' },
 *     { value: 'c', label: 'Option C' },
 *   ]}
 *   value={selectedValues}
 *   onChange={setSelectedValues}
 * />
 *
 * @example
 * // Horizontal group with error
 * <CheckboxGroup
 *   label="Coverage types"
 *   options={options}
 *   orientation="horizontal"
 *   error
 *   errorMessage="Please select at least one option"
 * />
 */
const CheckboxGroup = React.forwardRef<HTMLFieldSetElement, CheckboxGroupProps>(
  (
    {
      label,
      options,
      value: controlledValue,
      defaultValue = [],
      onChange,
      orientation = 'vertical',
      size = 'md',
      error = false,
      errorMessage,
      required = false,
      disabled = false,
      className = '',
      helperText,
      id: providedId,
      name: providedName,
    },
    ref
  ) => {
    const reactId = React.useId();
    const groupName = providedName || `checkbox-group-name-${reactId}`;

    const {
      groupId, currentValue: selectedValues, setValue,
      showError, message, messageId,
    } = useGroupState<string[]>({
      prefix: 'checkbox-group', providedId, controlledValue, defaultValue,
      onChange, error, errorMessage, helperText,
    });

    // Handle checkbox change
    const handleCheckboxChange = (optionValue: string, checked: boolean) => {
      const newValues = checked
        ? [...selectedValues, optionValue]
        : selectedValues.filter((v) => v !== optionValue);
      setValue(newValues);
    };

    return (
      <GroupFieldset
        ref={ref}
        groupId={groupId}
        className={className}
        messageId={messageId}
        error={error}
        label={label}
        required={required}
        orientation={orientation}
        message={message}
        showError={showError}
      >
        {options.map((option) => (
          <Checkbox
            key={option.value}
            name={groupName}
            value={option.value}
            label={option.label}
            checked={selectedValues.includes(option.value)}
            onChange={(e) => { handleCheckboxChange(option.value, e.target.checked); }}
            disabled={disabled || option.disabled}
            error={error}
            size={size}
          />
        ))}
      </GroupFieldset>
    );
  }
);

CheckboxGroup.displayName = 'CheckboxGroup';

export { CheckboxGroup };
